import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast, ToastOptions } from "react-toastify";
import ResourceService from "@app/resources/resourceService";
import { useNpmInvalidateQueries } from "@app/network-portfolio-management/services/npmQueries";
import { projectNoun } from "@app/network-portfolio-management/utils/projectTypeUtils";

const toastOptions: ToastOptions = { containerId: "z-index-9999" };

export interface UseBulkArchive {
  /** True while a bulk archive is being submitted; disables the action button / prevents double-submit. */
  isArchiving: boolean;
  /**
   * Archives the given projects (by resource id). Resolves true when at least one project was archived so the caller
   * can clear the selection; resolves false on a total failure so the selection is preserved for a retry.
   */
  archive: (projectIds: number[]) => Promise<boolean>;
}

/**
 * Drives the NPM bulk Archive action by reusing the existing resources/bulkArchive endpoint (via
 * {@link ResourceService}). Archive is a system-wide soft delete; the endpoint returns the ids that were actually
 * archived (which may be a subset — already-archived projects are skipped), so a partial archive surfaces a single
 * warning toast with both the archived and failed counts (a full archive gets a plain success toast).
 *
 * After a successful archive we re-query the project list against the current filter so the archived projects drop
 * out of the active cohort immediately.
 *
 * @param onComplete Invoked after at least one project is archived (used to clear the selection).
 */
export function useBulkArchive(onComplete: () => void): UseBulkArchive {
  const resourceService = useRef(new ResourceService()).current;
  const invalidateProjectLists = useNpmInvalidateQueries();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (projectIds: number[]) => resourceService.bulkArchive(projectIds),
    onError: (_error, projectIds) => {
      toast.error(`There was a problem archiving the selected ${projectNoun(projectIds.length)}. Please try again.`, toastOptions);
    },
    onSuccess: (archivedIds, projectIds) => {
      const archivedCount = archivedIds.length;
      if (archivedCount === 0) {
        // The request succeeded but nothing was archivable (e.g. all already archived) — treat as a failure:
        // no re-fetch, no selection clear, so the user can retry against the same selection.
        toast.error(`The selected ${projectNoun(projectIds.length)} could not be archived. Please try again.`, toastOptions);
        return;
      }

      // Partial success gets a single warning toast (archived + failed counts) to align with Classic's UX;
      // a full success gets the plain success toast.
      const failedCount = projectIds.length - archivedCount;
      if (failedCount > 0) {
        toast.warning(`${archivedCount} ${projectNoun(archivedCount)} archived, ${failedCount} ${projectNoun(failedCount)} could not be archived. Please check those projects.`, toastOptions);
      } else {
        toast.success(`${archivedCount} ${projectNoun(archivedCount)} archived.`, toastOptions);
      }

      // Re-fetch every project-list view against the current filter: the filtered IDs are re-evaluated (archived
      // projects drop out of the cohort), which cascades to the paged, grouped, and sorted views. Then clear
      // the selection.
      void invalidateProjectLists();
      onComplete();
    },
  });

  const archive = useCallback(async (projectIds: number[]): Promise<boolean> => {
    // Guard an empty selection and re-entry while a submit is in flight (the button is also disabled via isPending).
    if (projectIds.length === 0 || isPending) return false;
    try {
      const archivedIds = await mutateAsync(projectIds);
      return archivedIds.length > 0;
    } catch {
      return false; // onError already surfaced the toast; keep the selection for a retry.
    }
  }, [mutateAsync, isPending]);

  return { archive, isArchiving: isPending };
}
